import { useMemo } from "react";
import { Download } from "lucide-react";
import Breadcrumb from "components/shared/Breadcrumb/Breadcrumb";
import SearchBarRed from "components/shared/SearchBar/SearchBarRed";
import { DesktopPageTabsBar } from "components/shared/PageTabs/PageTabs";
import TableWithFilters from "components/shared/TableWithFilters/TableWithFilters";
import { Loader } from "../Common/Loader";
import ShowToast from "../Common/Toast";
import PayoutFilters from "./PayoutFilters";
import PayoutDetailDrawer from "./PayoutDetailDrawer";
import { PAYOUT_PAGE_TABS } from "./config";
import { TitleRow } from "components/core/Common/DesktopHelpers/DesktopHelpers";
import {
  DesktopPage,
  DesktopTitle,
  SectionHeaderRow,
  SectionTitle,
  PaymentAdviceButton,
  CellText,
  AmountCell,
  LinkCell,
  StatusPill,
} from "./styles.payoutDesktop";

export default function PayoutDesktop({
  activeTab,
  handleTabChange,
  isPayments,
  searchQuery,
  setSearchQuery,
  draftFilters,
  setDraftDueDate,
  setDraftPaidDate,
  setDraftStatus,
  statusFilterOptions,
  applyFilters,
  clearFilters,
  pageNumber,
  hasNextPage,
  goToPage,
  isLoading,
  paymentsTableRowData,
  transactionsTableRowData,
  openPayment,
  openTransaction,
  closeDrawer,
  openReferenceNumber,
  selectedTransaction,
  downloading,
  downloadError,
  setDownloadError,
  handleDownload,
}) {
  const paymentColumns = useMemo(
    () => [
      {
        id: "due_date",
        header: "Due date",
        meta: { width: 140 },
        cell: (info) => (
          <LinkCell onClick={() => openPayment(info.row.original.id)}>
            {info.row.original.dueDate}
          </LinkCell>
        ),
      },
      {
        id: "amount",
        header: "Amount",
        meta: { width: 140 },
        cell: (info) => (
          <AmountCell $negative={info.row.original.isDebit}>{info.row.original.amount}</AmountCell>
        ),
      },
      {
        id: "utr",
        header: "UTR",
        meta: { width: 200 },
        cell: (info) => <CellText>{info.row.original.utr}</CellText>,
      },
      {
        id: "remarks",
        header: "Remarks",
        meta: { width: 220 },
        cell: (info) => <CellText>{info.row.original.remarks}</CellText>,
      },
      {
        id: "status",
        header: "Status",
        meta: { width: 140 },
        cell: (info) =>
          info.row.original.tag ? (
            <StatusPill $status={info.row.original.tag}>{info.row.original.tag}</StatusPill>
          ) : (
            <CellText>-</CellText>
          ),
      },
    ],
    [openPayment]
  );

  const transactionColumns = useMemo(
    () => [
      {
        id: "transaction_type",
        header: "Transaction type",
        meta: { width: 180 },
        cell: (info) => (
          <LinkCell onClick={() => openTransaction(info.row.original.number)}>
            {info.row.original.transactionType}
          </LinkCell>
        ),
      },
      {
        id: "number",
        header: "Invoice/DN/CN number",
        meta: { width: 200 },
        cell: (info) => <CellText>{info.row.original.number}</CellText>,
      },
      {
        id: "remarks",
        header: "Remarks",
        meta: { width: 200 },
        cell: (info) => <CellText>{info.row.original.remarks}</CellText>,
      },
      {
        id: "amount",
        header: "Amount",
        meta: { width: 140 },
        cell: (info) => <AmountCell>{info.row.original.amount}</AmountCell>,
      },
      {
        id: "status",
        header: "Status",
        meta: { width: 140 },
        cell: (info) =>
          info.row.original.tag ? (
            <StatusPill $status={info.row.original.tag}>{info.row.original.tag}</StatusPill>
          ) : (
            <CellText>-</CellText>
          ),
      },
    ],
    [openTransaction]
  );

  return (
    <DesktopPage>
      <Breadcrumb items={[{ label: "Home" }, { label: "Payments" }]} />
      <TitleRow>
        <DesktopTitle>Payments</DesktopTitle>
        {!isPayments ? (
          <SearchBarRed
            placeholder="Search invoice, GRN, UTR..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        ) : null}
      </TitleRow>
      <DesktopPageTabsBar
        tabs={PAYOUT_PAGE_TABS}
        activeKey={activeTab}
        onChange={handleTabChange}
        ariaLabel="Payout pages"
      />
      <SectionHeaderRow>
        <SectionTitle>{isPayments ? "All payments" : "All transactions"}</SectionTitle>
        <PaymentAdviceButton type="button" onClick={handleDownload} disabled={downloading}>
          <span>Payment advice</span>
          <Download size={16} />
        </PaymentAdviceButton>
      </SectionHeaderRow>
      {isLoading && !(isPayments ? paymentsTableRowData : transactionsTableRowData).length ? (
        <Loader />
      ) : (
        <TableWithFilters
          filters={
            <PayoutFilters
              draftFilters={draftFilters}
              onDueDateChange={setDraftDueDate}
              onPaidDateChange={setDraftPaidDate}
              onStatusChange={setDraftStatus}
              statusOptions={statusFilterOptions}
              onApply={applyFilters}
              onClear={clearFilters}
            />
          }
          columns={isPayments ? paymentColumns : transactionColumns}
          data={isPayments ? paymentsTableRowData : transactionsTableRowData}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          emptyMessage={isPayments ? "No payments found" : "No transactions found"}
          page={pageNumber + 1}
          hasNextPage={hasNextPage}
          onPageChange={(nextPage) => goToPage(nextPage - 1)}
        />
      )}
      <PayoutDetailDrawer
        referenceNumber={openReferenceNumber}
        transaction={selectedTransaction}
        onClose={closeDrawer}
      />
      {downloadError ? (
        <ShowToast
          type="error"
          errorMsg={downloadError}
          onClose={() => setDownloadError("")}
        />
      ) : null}
    </DesktopPage>
  );
}
